import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionIcon from "@mui/icons-material/Description";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import PersonIcon from "@mui/icons-material/Person";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TextSnippetIcon from "@mui/icons-material/TextSnippet";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Box from "@mui/joy/Box";
import Card from "@mui/joy/Card";
import CardContent from "@mui/joy/CardContent";
import Chip from "@mui/joy/Chip";
import IconButton from "@mui/joy/IconButton";
import Stack from "@mui/joy/Stack";
import Typography from "@mui/joy/Typography";
import React from "react";
import { useNavigate } from "react-router-dom";
import { UnixTimestamp } from "../../../models/common";
import { DocumentWithDetails } from "../../../models/document";
import { Person } from "../../../models/person";
import { unixToDayjs } from "../../../shared/utils/time";

interface DocumentsListProps {
  documents: DocumentWithDetails[];
  persons?: Person[];
}

const getFileIcon = (contentType: string) => {
  if (contentType.includes("pdf")) {
    return <PictureAsPdfIcon color="error" />;
  } else if (contentType.includes("text")) {
    return <TextSnippetIcon color="primary" />;
  } else if (contentType.includes("word") || contentType.includes("document")) {
    return <DescriptionIcon color="info" />;
  }
  return <DescriptionIcon />;
};

const getFileTypeChip = (contentType: string) => {
  if (contentType.includes("pdf")) {
    return (
      <Chip color="danger" size="sm">
        PDF
      </Chip>
    );
  } else if (contentType.includes("text")) {
    return (
      <Chip color="primary" size="sm">
        TXT
      </Chip>
    );
  } else if (contentType.includes("word") || contentType.includes("document")) {
    return (
      <Chip color="neutral" size="sm">
        DOC
      </Chip>
    );
  }
  return (
    <Chip color="neutral" size="sm">
      DOC
    </Chip>
  );
};

export const DocumentsList: React.FC<DocumentsListProps> = ({ documents, persons = [] }) => {
  const navigate = useNavigate();

  const handlePatientClick = (document: DocumentWithDetails) => {
    if (document.person_id) {
      navigate(`/patient/${document.person_id}`);
    }
  };

  const handleMetadataReview = (document: DocumentWithDetails) => {
    navigate(`/document/${document.document_id}`);
  };

  const handleFactsReview = (document: DocumentWithDetails) => {
    // Navigate to the note if available
    if (document.note_id) {
      navigate(`/note/${document.note_id}`);
    }
  };

  const formatDate = (timestamp: UnixTimestamp) => {
    try {
      return unixToDayjs(timestamp)?.format("DD MMM YYYY HH:mm") || "Unknown date";
    } catch {
      return "Unknown date";
    }
  };

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "repeat(1, 1fr)",
          sm: "repeat(2, 1fr)",
          md: "repeat(3, 1fr)",
          lg: "repeat(4, 1fr)",
        },
        gap: 2,
      }}
    >
      {documents.map((document) => (
        <Box key={document.document_id}>
          <Card
            variant="outlined"
            sx={{
              p: 2,
              height: "200px",
              display: "flex",
              flexDirection: "column",
              transition: "all 0.2s ease-in-out",
              border: "1px solid",
              borderColor: "divider",
              "&:hover": {
                boxShadow: "sm",
                transform: "translateY(-1px)",
                borderColor: "primary.300",
              },
            }}
          >
            <CardContent sx={{ p: 0, flex: 1, display: "flex", flexDirection: "column" }}>
              {/* Header with file icon and action buttons */}
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 2 }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  {getFileIcon(document.content_type)}
                  {getFileTypeChip(document.content_type)}
                </Stack>
                <Stack direction="row" spacing={1}>
                  <IconButton
                    size="sm"
                    variant="soft"
                    onClick={() => navigate(`/document/${document.document_id}`)}
                    title="View document"
                  >
                    <VisibilityIcon />
                  </IconButton>
                  {document.person_id && (
                    <IconButton
                      size="sm"
                      variant="soft"
                      color="primary"
                      onClick={() => handlePatientClick(document)}
                      title="View patient"
                    >
                      <PersonIcon />
                    </IconButton>
                  )}
                  {document.note_id && (
                    <IconButton
                      size="sm"
                      variant="soft"
                      color="success"
                      onClick={() => handleFactsReview(document)}
                      title={`View note with ${document.fact_count} facts`}
                    >
                      <FactCheckIcon />
                    </IconButton>
                  )}
                </Stack>
              </Stack>

              {/* Document title */}
              <Typography level="title-md" noWrap title={document.filename}>
                {document.filename}
              </Typography>

              {/* Upload date */}
              <Typography level="body-xs" color="neutral" sx={{ mt: 1, mb: 2 }}>
                Uploaded: {formatDate(document.upload_date)}
              </Typography>

              {/* Status chips */}
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
                sx={{ mb: "auto" }}
              >
                {document.reviewed ? (
                  <Chip
                    size="sm"
                    variant="outlined"
                    color="success"
                    startDecorator={<CheckCircleIcon />}
                    sx={{
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: "success.softHoverBg",
                      },
                    }}
                    onClick={() => handleMetadataReview(document)}
                  >
                    Metadata Reviewed
                  </Chip>
                ) : (
                  <Chip
                    size="sm"
                    variant="outlined"
                    color="warning"
                    startDecorator={<CloseIcon />}
                    sx={{
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: "warning.softHoverBg",
                      },
                    }}
                    onClick={() => handleMetadataReview(document)}
                  >
                    Metadata Pending
                  </Chip>
                )}
                {document.note_id && document.fact_count > 0 && (
                  <Chip
                    size="sm"
                    variant="outlined"
                    color={document.note_validated ? "success" : "warning"}
                    startDecorator={document.note_validated ? <CheckCircleIcon /> : <CloseIcon />}
                    sx={{
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: document.note_validated
                          ? "success.softHoverBg"
                          : "warning.softHoverBg",
                      },
                    }}
                    onClick={() => handleFactsReview(document)}
                  >
                    {document.fact_count} Facts
                  </Chip>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Box>
      ))}
    </Box>
  );
};
